/* Shared component-level maps for notification-like components (Alert, Toast). */

import { Spinner } from '@chakra-ui/react/spinner';
import { type ElementType } from 'react';

import IconCheck from '../../icons/IconCheck';
import IconErrorCircleFilled from '../../icons/IconErrorCircleFilled';
import IconInfoCircle from '../../icons/IconInfoCircle';
import IconSparkleFilled from '../../icons/IconSparkleFilled';
import IconWarning from '../../icons/IconWarning';
import { type NotificationVariant } from '../../theme/common/AlertAndToast.common';

export type NotificationAction = {
  href?: string;
  /** Only with `href`: opens the link in a new tab and adds the external-link arrow. */
  isExternal?: boolean;
  label: string;
  onClick?: () => void;
  /**
   * Disabled or loading state of the action button.
   *
   * Known gap: not every consumer can honour the full range. Alert, Toast, PromoBanner and Ribbon
   * actions render via `BitkitColorButton` and support both. `BitkitNoteCard`'s **link** actions (`href`
   * set) render via `BitkitButton`, whose anchor variant excludes `loading` at the type level — so
   * `'loading'` degrades to no state there. That's an implementation limit of `BitkitButton`'s
   * anchor branch, not a rule about links: loading anchors work fine on `BitkitColorButton` (`href`
   * kept, pointer events off, activation guarded). Closing it means teaching `BitkitButton` the
   * same trick — its own change.
   */
  state?: 'disabled' | 'loading';
  /** Anchor target. Ignored when `isExternal` is set. */
  target?: HTMLAnchorElement['target'];
};

export const BUTTON_COLORS_MAP = {
  ai: 'indigo',
  critical: 'red',
  info: 'blue',
  progress: 'purple',
  success: 'green',
  warning: 'yellow',
} as const satisfies Record<NotificationVariant, string>;

export const ICON_COMPONENTS_MAP: Record<NotificationVariant, ElementType> = {
  ai: IconSparkleFilled,
  critical: IconErrorCircleFilled,
  info: IconInfoCircle,
  progress: Spinner,
  success: IconCheck,
  warning: IconWarning,
};
